/**
 * The active workout: a Zustand 5 store with `persist` (plan D22), and the
 * actions that move it.
 *
 * ── THE STORE IS THE ONLY OWNER OF THE ACTIVE SESSION ──────────────────────
 * Screens read it through `selectors.ts` and change it through these actions;
 * none keeps a copy. The state is ONE field, `session`, persisted whole: every
 * transition is a pure function in `session-model.ts` that returns a new
 * session, so a change is one `setState` and one ordered write to disk.
 *
 * ── EVERY MUTATING ACTION IS ONE CALL: LOCAL STATE AND ITS OUTBOX ITEM ─────
 * The new session goes into the store and the matching write into the outbox
 * (`outbox-bridge.ts`) in the same synchronous step, local first. Local-only
 * moves (the rest, the cursor, the scratch numbers, a today-only swap) have
 * no server write and enqueue nothing.
 *
 * ── NOTHING RUNS BEFORE HYDRATION (D22) ────────────────────────────────────
 * On Preferences hydration is async. An action taken before it finishes would
 * see "no session" and `persist` would write that over the stored one — the
 * silent loss the plan names. Every action refuses with
 * `WorkoutNotHydratedError` until the store has hydrated; screens render a
 * skeleton until `useWorkoutHydrated()` says so, which makes the refusal
 * unreachable from the UI and loud if it is ever reached.
 *
 * ── `reset()` NEVER TOUCHES THE OUTBOX ─────────────────────────────────────
 * Forgetting the session on this device and dropping its unsynced writes are
 * different acts. Sign-out asks before the second (T11's session controller).
 */
import type { KeyValueStorage } from "@repo/client/i18n";
import type { OutboxItem } from "@repo/client/offline";
import type { SwapScope } from "@repo/client/session";
import type {
  PlanItemView,
  PlanSessionView,
  SessionAdjustment,
  SessionReadiness,
  SetKind,
  SetSide,
} from "@repo/contract/training";
import { persist } from "zustand/middleware";
import { createStore, type StoreApi } from "zustand/vanilla";
import type { RecordFloor } from "./live-records";
import type { WorkoutOutbox } from "./outbox-bridge";
import {
  createWorkoutStorage,
  type PersistedWorkout,
  WORKOUT_STORAGE_KEY,
  WORKOUT_STORE_VERSION,
  type WorkoutStorageReport,
} from "./persisted-storage";
import {
  addExercise as addExerciseTo,
  addSet as addSetTo,
  adjustRest as adjustRestOf,
  adoptServerSets,
  chooseNextSet as chooseNextSetOf,
  confirmValues as confirmValuesOf,
  correctSet as correctSetOf,
  deferCurrent as deferCurrentOf,
  endRest,
  type FeedbackState,
  fillBelow as fillBelowOf,
  finish,
  forgetSet as forgetSetOf,
  jumpTo as jumpToOf,
  liveOf,
  loggedSetFor,
  logSet as logSetOf,
  nextOpenExercise,
  oweExclusion as oweExclusionOf,
  type PlannedWarmup,
  type PreviousSet,
  pauseClock,
  planWarmups as planWarmupsOf,
  type QuickSessionMeta,
  type RemoteSession,
  type RemovedSet,
  rebaseItem as rebaseItemOf,
  removePendingSet as removePendingSetOf,
  restoreRemovedSet as restoreRemovedSetOf,
  resumeClock,
  resumeSession,
  revertCorrection as revertCorrectionOf,
  type ServerSet,
  type SetCorrection,
  type SetMeasures,
  type StartableSession,
  setSwapScope as setSwapScopeOf,
  settleAdjustments,
  startSession,
  suggestionAt,
  swapToday as swapTodayOf,
  type TodaySwap,
  undoSet as undoSetOf,
  type WorkoutSession,
  withdrawAdded as withdrawAddedOf,
} from "./session-model";
import { uuidv7 } from "./uuid-v7";

export interface WorkoutState {
  readonly session: WorkoutSession | null;
}

export interface HydrationStatus {
  /** Set when the stored session could not be read; writes are off. */
  readonly error: unknown;
  readonly hydrated: boolean;
}

export type WorkoutStore = StoreApi<WorkoutState> & {
  /** Hydration, as `onRehydrateStorage` reports it — errors included. */
  readonly hydration: {
    getStatus(): HydrationStatus;
    subscribe(listener: () => void): () => void;
    /** Resolves once hydration has finished, successfully or not. */
    whenHydrated(): Promise<void>;
  };
  readonly persist: {
    hasHydrated(): boolean;
    rehydrate(): Promise<void> | void;
  };
};

export interface WorkoutStoreOptions {
  readonly key?: string;
  readonly now?: () => Date;
  readonly onProblem?: (report: WorkoutStorageReport) => void;
  readonly storage: KeyValueStorage;
}

export function createWorkoutStore(options: WorkoutStoreOptions): WorkoutStore {
  const storage = createWorkoutStorage(options.storage, {
    ...(options.now === undefined ? {} : { now: options.now }),
    ...(options.onProblem === undefined
      ? {}
      : { onProblem: options.onProblem }),
  });
  let status: HydrationStatus = { error: null, hydrated: false };
  const listeners = new Set<() => void>();
  let resolveHydrated: () => void = () => undefined;
  const hydrated = new Promise<void>((resolve) => {
    resolveHydrated = resolve;
  });
  const settle = (error: unknown) => {
    status = { error, hydrated: true };
    resolveHydrated();
    for (const listener of listeners) {
      listener();
    }
  };

  const store = createStore<WorkoutState>()(
    persist<WorkoutState, [], [], PersistedWorkout>(() => ({ session: null }), {
      migrate: storage.migrate,
      name: options.key ?? WORKOUT_STORAGE_KEY,
      // A rejected hydration still ends in `settle`: without it the gate
      // would hold the skeleton forever. `error` says what happened.
      onRehydrateStorage: () => (_state, error) => {
        settle(error ?? (storage.readOnly() ? "read failed" : null));
      },
      partialize: (state) => ({ session: state.session }),
      storage,
      version: WORKOUT_STORE_VERSION,
    })
  );

  return Object.assign(store, {
    hydration: {
      getStatus: () => status,
      subscribe: (listener: () => void) => {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      whenHydrated: () => hydrated,
    },
  }) as WorkoutStore;
}

/** An action ran before the stored session was read (see the header). */
export class WorkoutNotHydratedError extends Error {
  constructor() {
    super("The workout store acted before it hydrated");
    this.name = "WorkoutNotHydratedError";
  }
}

export interface WorkoutClock {
  readonly newId: (now: number) => string;
  /** Epoch ms. The edge of the store reads the clock; the model never does. */
  readonly now: () => number;
}

export const systemClock: WorkoutClock = {
  newId: (now) => uuidv7(now),
  now: () => Date.now(),
};

/**
 * Every action. Each returns once its outbox write (if any) is persisted;
 * the local state has changed before it returns at all. A rejected promise is
 * a write that did not reach the queue, which the caller must surface.
 */
export interface WorkoutActions {
  /**
   * An exercise for today only (`D254`, `addExercise`). LOCAL ONLY, like a
   * today swap: its sets are what the server records. `false` when it was
   * not added — the session ended, the exercise is already today's, or the
   * day took its most.
   */
  addExercise(item: PlanItemView): boolean;
  addSet(i: number): void;
  adjustRest(deltaSeconds: number): void;
  /**
   * The kind sheet (`D209`, B1): what the NEXT set is logged as and on which
   * side. LOCAL ONLY — the kind travels on the set's own record.
   */
  chooseNextSet(choice: {
    readonly kind?: SetKind | null;
    readonly side?: SetSide | null;
  }): void;
  complete(): Promise<void>;
  /**
   * "Sim" to a plausibility question (`D244`): the keys are not asked about
   * again in this workout. LOCAL ONLY — the value travels on its set.
   */
  confirmValues(keys: readonly string[]): void;
  correctSet(id: string, values: SetCorrection): Promise<void>;
  /**
   * "Fazer depois": on to the next open exercise, the rest still running
   * (`deferCurrent`). LOCAL ONLY, like `swapToday`: the server records sets,
   * not the order they come in.
   */
  deferCurrent(): void;
  /**
   * The forgotten workout ended at the moment the person chose (`D245`):
   * `at` epoch ms, never before the last set (`finish` raises it). The end
   * is the ordinary one — `complete` when nothing is left open, else
   * `early` — through the same outbox write and the same route, so the
   * follow-ups are promised exactly as for "Encerrar" (`D116`).
   */
  endAt(at: number): Promise<void>;
  endEarly(): Promise<void>;
  /**
   * "Preencher abaixo" (`D209`, B4): the set's numbers become the
   * suggestion for the rest of its exercise. LOCAL ONLY — nothing is logged.
   */
  fillBelow(setId: string): void;
  /** The rest reached zero in the foreground (T54's view, the rest alarm). */
  finishRest(): void;
  /**
   * The quarantine sheet's "Descartar" (D23, T23): a write the server refused
   * leaves the queue AND stops being shown as done. A discarded `set.record`
   * that only left the queue kept counting towards the summary and sent a
   * later undo to delete a row the server never stored.
   *
   * It drops the item itself, whatever the kind — the caller does not discard
   * again.
   */
  forgetDiscarded(item: OutboxItem): Promise<void>;
  jumpTo(i: number): void;
  /**
   * "Continuar treinando" to the forgotten-workout question (`D245`).
   * LOCAL ONLY: it moves when the question may be asked again.
   */
  keepOpen(): void;
  logSet(measures?: SetMeasures): Promise<void>;
  /**
   * The swap at `i` answered "never", its plan replace landed and its
   * exclusion did not: the exclusion is owed, across a relaunch, until the
   * next `setSwapScope` for `i`. LOCAL ONLY, like the answer itself.
   */
  oweExclusion(i: number): void;
  /** Pause the session's clock (`D245`). LOCAL ONLY until the end. */
  pause(): void;
  /**
   * "Adicionar aquecimento" (`D209`, B2): the calculator's sets before the
   * working sets of `i`, while nothing of `i` is logged. LOCAL ONLY — each
   * warm-up is written when it is logged, as a `kind: "warmup"` record.
   */
  planWarmups(i: number, planned: readonly PlannedWarmup[]): void;
  /**
   * The plan item behind `i` was replaced and the one that exists now carries
   * `itemId` (T31). LOCAL ONLY: the plan write already happened over the wire,
   * and the session's `items` are its own copy of the prescription, not a
   * server record — so nothing is enqueued.
   */
  rebaseItem(i: number, itemId: string): void;
  /**
   * The server's sets for `sessionId` taken in (T-188, `adoptServerSets`):
   * the rows a kill left on the server and off this device. Nothing when the
   * session is another one by now. When they were the day's last open sets,
   * the end they would have queued is queued here.
   */
  reconcileSets(sessionId: string, sets: readonly ServerSet[]): Promise<void>;
  /** Returns the snapshot the toast's "Desfazer" hands back, or `null`. */
  removePendingSet(i: number): Promise<RemovedSet | null>;
  /** Forgets the session on this device. The outbox is not touched. */
  reset(): void;
  restoreRemovedSet(snapshot: RemovedSet): void;
  /** Run the paused clock again (`D245`). LOCAL ONLY. */
  resume(): void;
  /**
   * A workout open on another device, continued here (T09, `resumeSession`).
   * Enqueues NO start — the server holds the session already, and a second
   * start is exactly the 409 this exists to avoid. The one write it can queue
   * is the end, when the server's sets already finished the day, as
   * `reconcileSets` does. Returns the session id; an active session is kept,
   * not replaced, as `start` keeps it.
   */
  resumeRemote(
    planSession: Pick<PlanSessionView, "items" | "label" | "sessionId">,
    remote: RemoteSession
  ): Promise<string>;
  /**
   * The end-of-workout offer to carry today's numbers into the plan
   * (`D247`) was answered, either way, on an ENDED session. LOCAL ONLY.
   */
  setCarried(): void;
  /** The scratch numbers the next set is logged with. */
  setDraft(values: {
    readonly kg?: number | null;
    readonly reps?: number;
  }): void;
  setFeedback(state: FeedbackState): void;
  /**
   * The last session's sets, per exercise id, for the suggestions — and the
   * records of each before today, for the live record (`D243`).
   */
  setPrevious(
    previous: Readonly<Record<string, readonly PreviousSet[]>>,
    records?: Readonly<Record<string, RecordFloor>>
  ): void;
  /**
   * The end-of-workout answer for the swap at `i`, kept across a relaunch.
   * LOCAL ONLY, and taken after the end: the plan write it leads to is its
   * own request.
   */
  setSwapScope(i: number, scope: SwapScope): void;
  /** The server's cut once the start synced; `null` keeps the preview. */
  settleAdjustments(
    sessionId: string,
    adjustments: readonly SessionAdjustment[] | null
  ): void;
  skipRest(): void;
  /**
   * Returns the session id; an active session is kept, not replaced. With
   * `holdUntil` the start is queued HELD (`D194`): the workout runs at once
   * and the start waits for the readiness check's answer, or that instant.
   * With `quick` it is a quick workout's (`D249`): no plan session, its
   * lines' swaps carried, the check-in already in its numbers.
   */
  start(
    planSession: StartableSession,
    readiness: SessionReadiness | null,
    options?: {
      /** A blank workout (`D254`): no line yet, and no end of its own. */
      readonly blank?: boolean;
      readonly holdUntil?: Date;
      readonly quick?: QuickSessionMeta;
    }
  ): Promise<string>;
  swapToday(i: number, swap: TodaySwap | string | null): void;
  undoSet(): Promise<void>;
  /** The add's "Desfazer" (`withdrawAdded`). LOCAL ONLY. */
  withdrawAdded(itemId: string): void;
}

/** The `set.correct` body's replacement id, read defensively (it came off disk). */
function revertedFrom(
  session: WorkoutSession,
  body: unknown
): WorkoutSession | null {
  const id = (body as { readonly id?: unknown } | null)?.id;
  return typeof id === "string" ? revertCorrectionOf(session, id) : null;
}

/** What the actions read of the device's workout settings (`D209`). */
export interface WorkoutActionSettings {
  /** The rest after a warm-up set, seconds (B7). */
  readonly warmupRestSeconds: () => number | undefined;
}

const NO_SETTINGS: WorkoutActionSettings = {
  warmupRestSeconds: () => undefined,
};

export function createWorkoutActions(
  store: WorkoutStore,
  outbox: WorkoutOutbox,
  clock: WorkoutClock = systemClock,
  settings: WorkoutActionSettings = NO_SETTINGS
): WorkoutActions {
  const current = (): WorkoutSession | null => {
    // The status `onRehydrateStorage` settles, not `persist.hasHydrated()`:
    // persist flips its own flag only AFTER that callback, so a listener
    // woken by it would still read "not hydrated" there.
    if (!store.hydration.getStatus().hydrated) {
      throw new WorkoutNotHydratedError();
    }
    return store.getState().session;
  };
  const put = (session: WorkoutSession | null) => {
    store.setState({ session });
  };
  /** A local-only move: apply, or do nothing. */
  const local = (move: (session: WorkoutSession) => WorkoutSession | null) => {
    const session = current();
    const next = session === null ? null : move(session);
    if (next !== null) {
      put(next);
    }
  };
  /**
   * A refused `set.record` off the workout and out of the queue. `false` when
   * either half could not happen — the set belongs to a session this device no
   * longer holds, or its record has already left the queue — and the caller
   * then drops the item on its own.
   */
  const forgetRecord = async (
    session: WorkoutSession,
    setId: string
  ): Promise<boolean> => {
    const set = loggedSetFor(session, setId);
    const forgotten = set === null ? null : forgetSetOf(session, setId);
    if (set === null || forgotten === null) {
      return false;
    }
    put(forgotten);
    // ONE all-or-nothing withdrawal (T27): the refused record and whatever
    // queued behind it for this set, which the quarantine held back.
    return await outbox.forget(set);
  };
  const end = (
    reason: "complete" | "early",
    at: number = clock.now()
  ): Promise<void> => {
    const session = current();
    const next = session === null ? null : finish(session, reason, at);
    if (next === null) {
      return Promise.resolve();
    }
    put(next);
    return outbox.end(next);
  };

  return {
    addExercise(item) {
      const session = current();
      const next = session === null ? null : addExerciseTo(session, item);
      if (next === null) {
        return false;
      }
      put(next);
      return true;
    },
    addSet: (i) => local((s) => addSetTo(s, i)),
    adjustRest: (delta) => local((s) => adjustRestOf(s, delta, clock.now())),
    chooseNextSet: (choice) => local((s) => chooseNextSetOf(s, choice)),
    complete: () => end("complete"),
    confirmValues: (keys) => local((s) => confirmValuesOf(s, keys)),
    correctSet(id, values) {
      const session = current();
      if (session === null) {
        return Promise.resolve();
      }
      const now = clock.now();
      const corrected = correctSetOf(session, id, values, {
        id: clock.newId(now),
      });
      if (corrected === null) {
        return Promise.resolve();
      }
      put(corrected.session);
      return outbox.correct(
        session.sessionId,
        corrected.previousId,
        corrected.set,
        values
      );
    },
    deferCurrent: () => local((s) => deferCurrentOf(s, liveOf(s))),
    endEarly: () => end("early"),
    endAt(at) {
      const session = current();
      if (session === null) {
        return Promise.resolve();
      }
      const live = liveOf(session);
      const open = nextOpenExercise(session, live, session.exIndex) >= 0;
      return end(open ? "early" : "complete", Math.min(at, clock.now()));
    },
    fillBelow: (setId) => local((s) => fillBelowOf(s, setId)),
    finishRest: () =>
      local((s) =>
        s.restEndsAt !== null && s.restEndsAt <= clock.now() ? endRest(s) : null
      ),
    async forgetDiscarded(item) {
      // Unlike every other action this one WAITS for hydration instead of
      // refusing (see the header). The person's "Descartar" has to drop the
      // write either way, and dropping it while the session is still unknown
      // would leave on screen exactly the set this action takes off.
      await store.hydration.whenHydrated();
      const { session } = store.getState();
      if (session !== null) {
        if (
          item.kind === "set.record" &&
          (await forgetRecord(session, item.entityId))
        ) {
          return;
        }
        if (item.kind === "set.correct") {
          const reverted = revertedFrom(session, item.body);
          if (reverted !== null) {
            put(reverted);
          }
        }
      }
      // `set.delete`: the undo already took the set off this device and the
      // server keeps its row — putting it back locally is out of scope. Every
      // other kind, and every fall-through above, is the queue's alone.
      await outbox.discard(item.id);
    },
    jumpTo: (i) => local((s) => jumpToOf(s, i)),
    keepOpen: () =>
      local((s) =>
        s.ended === null ? { ...s, keptOpenAt: clock.now() } : null
      ),
    logSet(measures = {}) {
      const session = current();
      if (session === null) {
        return Promise.resolve();
      }
      const now = clock.now();
      const warmupRestSeconds = settings.warmupRestSeconds();
      const logged = logSetOf(session, measures, {
        id: clock.newId(now),
        now,
        ...(warmupRestSeconds === undefined ? {} : { warmupRestSeconds }),
      });
      if (logged === null) {
        return Promise.resolve();
      }
      put(logged.session);
      // The day's last set carries the end behind it in the SAME outbox
      // write: as two, a kill between them left the session open on the
      // server with no end queued (`T-196`).
      return logged.completed
        ? outbox.recordAndEnd(session.sessionId, logged.set, logged.session)
        : outbox.record(session.sessionId, logged.set);
    },
    oweExclusion: (i) => local((s) => oweExclusionOf(s, i)),
    pause: () => local((s) => pauseClock(s, clock.now())),
    planWarmups: (i, planned) => local((s) => planWarmupsOf(s, i, planned)),
    rebaseItem: (i, itemId) => local((s) => rebaseItemOf(s, i, itemId)),
    reconcileSets(sessionId, sets) {
      const session = current();
      if (session === null || session.sessionId !== sessionId) {
        return Promise.resolve();
      }
      const adopted = adoptServerSets(session, sets, clock.now());
      if (adopted === null) {
        return Promise.resolve();
      }
      put(adopted.session);
      return adopted.completed
        ? outbox.end(adopted.session)
        : Promise.resolve();
    },
    removePendingSet(i) {
      const session = current();
      if (session === null) {
        return Promise.resolve(null);
      }
      const removed = removePendingSetOf(session, i, clock.now());
      if (removed === null) {
        return Promise.resolve(null);
      }
      put(removed.after);
      if (!removed.completed) {
        return Promise.resolve(removed);
      }
      return outbox.end(removed.after).then(() => removed);
    },
    reset: () => {
      current();
      put(null);
    },
    resumeRemote(planSession, remote) {
      const session = current();
      if (session !== null && session.ended === null) {
        return Promise.resolve(session.sessionId);
      }
      const resumed = resumeSession(planSession, remote, clock.now());
      put(resumed.session);
      return resumed.completed
        ? outbox.end(resumed.session).then(() => resumed.session.sessionId)
        : Promise.resolve(resumed.session.sessionId);
    },
    restoreRemovedSet: (snapshot) =>
      local((s) => restoreRemovedSetOf(s, snapshot)),
    resume: () => local((s) => resumeClock(s, clock.now())),
    setDraft: (values) =>
      local((s) => ({
        ...s,
        ...(values.kg === undefined ? {} : { kg: values.kg }),
        ...(values.reps === undefined ? {} : { reps: values.reps }),
      })),
    setCarried: () =>
      local((s) =>
        s.ended === null || s.carried === true ? null : { ...s, carried: true }
      ),
    setFeedback: (feedback) =>
      local((s) => (s.feedback === feedback ? null : { ...s, feedback })),
    setPrevious: (previous, records) =>
      local((s) => {
        const next = {
          ...s,
          previous,
          ...(records === undefined ? {} : { records }),
        };
        // The first set of the current exercise has not been touched yet:
        // re-suggest it from the history that just arrived.
        return (s.logged[s.exIndex]?.length ?? 0) === 0 && s.phase === "log"
          ? { ...next, ...suggestionAt(next, s.exIndex) }
          : next;
      }),
    setSwapScope: (i, scope) => local((s) => setSwapScopeOf(s, i, scope)),
    settleAdjustments: (sessionId, adjustments) =>
      local((s) => settleAdjustments(s, sessionId, adjustments)),
    skipRest: () => local(endRest),
    start(planSession, readiness, options = {}) {
      const session = current();
      if (session !== null && session.ended === null) {
        return Promise.resolve(session.sessionId);
      }
      const now = clock.now();
      const next = startSession(planSession, readiness, {
        blank: options.blank,
        id: clock.newId(now),
        now,
        quick: options.quick,
      });
      put(next);
      return outbox.start(next, options.holdUntil).then(() => next.sessionId);
    },
    swapToday: (i, swap) =>
      local((s) =>
        swapTodayOf(
          s,
          i,
          typeof swap === "string" ? { exerciseId: swap } : swap
        )
      ),
    undoSet() {
      const session = current();
      if (session === null) {
        return Promise.resolve();
      }
      const undone = undoSetOf(session);
      if (undone === null) {
        return Promise.resolve();
      }
      put(undone.session);
      return outbox.remove(session.sessionId, undone.set).then(() => undefined);
    },
    withdrawAdded: (itemId) => local((s) => withdrawAddedOf(s, itemId)),
  };
}
